import { useState, useCallback } from 'react';

interface CarbonLossCardProps {
  pf: number | null;
}

export default function CarbonLossCard({ pf }: CarbonLossCardProps) {
  const [cStored, setCStored] = useState('');
  const [cLoss, setCLoss] = useState<number | null>(null);
  const [cPersistent, setCPersistent] = useState<number | null>(null);
  const [error, setError] = useState('');

  const handleCalculate = useCallback(() => {
    setError('');

    if (pf === null) {
      setError('请先在上方计算 PF 值');
      return;
    }

    const stored = parseFloat(cStored);
    if (Number.isNaN(stored) || stored <= 0) {
      setError('请输入有效的碳储存量（C_stored > 0）');
      return;
    }

    const loss = stored * (100 - pf) / 100;
    const persistent = stored * pf / 100;

    setCLoss(parseFloat(loss.toFixed(4)));
    setCPersistent(parseFloat(persistent.toFixed(4)));
  }, [pf, cStored]);

  const persistentRatio = pf !== null ? pf : 0;
  const lossRatio = pf !== null ? 100 - pf : 0;

  return (
    <div className="bg-background-50 rounded-2xl p-5 md:p-8 border border-background-200/70 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-accent-500/15 flex items-center justify-center">
          <i className="ri-scales-line text-accent-600 text-xl" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">碳损失计算器</h2>
          <p className="text-sm text-foreground-600 mt-0.5">Equation 6.3 — 持久碳损失量</p>
        </div>
      </div>

      <div className="mb-5 p-4 rounded-xl bg-secondary-50 border border-secondary-100">
        <p className="text-sm text-foreground-700 font-mono text-center">
          C_loss = C_stored × (100 − PF) / 100
        </p>
      </div>

      <div className="space-y-2 mb-4">
        <label className="block text-sm font-medium text-foreground-800">
          碳储存量 C_stored
          <span className="ml-2 text-xs text-foreground-500 font-normal">(单位与 PF 结果一致)</span>
        </label>
        <input
          type="number"
          step="any"
          min={0}
          placeholder="例如 1000"
          value={cStored}
          onChange={(e) => { setCStored(e.target.value); setError(''); }}
          className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
        />
      </div>

      {pf !== null && (
        <div className="mb-4 p-3 rounded-lg bg-primary-50 border border-primary-100 flex items-center gap-2">
          <i className="ri-check-line text-primary-600 text-sm" />
          <span className="text-sm text-primary-800">
            使用 PF = <strong className="text-primary-700">{pf.toFixed(2)}%</strong>
          </span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <button
        onClick={handleCalculate}
        className="w-full bg-accent-500 hover:bg-accent-600 text-white font-medium text-sm rounded-xl py-3.5 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
      >
        <i className="ri-calculator-line" />
        计算碳损失
      </button>

      {(cLoss !== null && cPersistent !== null) && (
        <div className="mt-6 animate-fade-slide-up">
          {/* Visual bar */}
          <div className="mb-5">
            <div className="flex justify-between text-xs text-foreground-600 mb-2">
              <span>持久碳 {persistentRatio.toFixed(1)}%</span>
              <span>损失碳 {lossRatio.toFixed(1)}%</span>
            </div>
            <div className="h-4 rounded-full bg-background-200 overflow-hidden flex">
              <div
                className="h-full bg-primary-500 transition-all duration-700"
                style={{ width: `${persistentRatio}%` }}
              />
              <div
                className="h-full bg-red-400 transition-all duration-700"
                style={{ width: `${lossRatio}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-primary-50 border border-primary-200 text-center">
              <p className="text-xs text-primary-700 mb-1">持久碳量</p>
              <p className="text-2xl font-bold text-primary-700">{cPersistent}</p>
              <p className="text-xs text-primary-600 mt-1">C_stored × PF / 100</p>
            </div>
            <div className="p-5 rounded-xl bg-red-50 border border-red-200 text-center">
              <p className="text-xs text-red-700 mb-1">碳损失量 C_loss</p>
              <p className="text-2xl font-bold text-red-600">{cLoss}</p>
              <p className="text-xs text-red-600 mt-1">C_stored × (100−PF) / 100</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}